import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import { useSearchParams, useParams } from 'react-router'
import styled from 'styled-components'

import {
  useFetchPartnerAppRequestsQuery,
} from 'api/partnerAppRequests'

import { useFetchTicketQuery } from 'api/tickets'

import colors from 'components/utils/colors'

import MainWrap from 'components/Shared/Skeleton/MainWrap/Index'
import Content from 'components/Shared/Skeleton/Content/Index'
import Header from 'components/Shared/Header/Index'
import Pagination from 'components/Shared/Pagination/Index'
import PartnerAppRequestsTable from './PartnerAppRequestsTable'
import PartnerAppRequestsSidebar from './PartnerAppRequestsSidebar'

import {
  RouteTicketingSystems,
  RouteTicketingSystemsTickets,
  RouteTicketingSystemsTicket,
} from 'routes'

import ErrorPage404 from 'components/App/ErrorPage/ErrorPage404'

const CountRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 14px;
  padding-bottom: 10px;
  color: ${colors.secondary20};
`

const StyledCount = styled.label`
  font-weight: 600;
  font-size: 12px;
`

const TicketRequestsPage = () => {
  const [selectedRequest, setSelectedRequest] = useState(null)
  const params = useParams()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  const activePage = parseInt(searchParams.get('page')) || 1
  const ticketingSystemDisplayId = params.ticketingSystemDisplayId || null
  const ticketId = params.ticketId || null
  const { data, isFetching: isRequestsFetching } = useFetchPartnerAppRequestsQuery({
    page: activePage,
    ticketId: ticketId,
  })

  const { data: ticketData, isFetching: isTicketFetching } = useFetchTicketQuery({
    ticketingSystemDisplayId: ticketingSystemDisplayId,
    ticketId: ticketId,
  })

  const ticket = ticketData?.ticket || {}

  const requests = data?.partnerAppRequests || []
  const totalPages = data?.meta.totalPages || 1
  const requestsCount = data?.meta.partnerAppRequestsCount || 0

  useEffect(() => {
    setSelectedRequest(null)
  }, [isRequestsFetching])

  const getBreadcrumbs = () => {
    const ticketingSystemsUrl = RouteTicketingSystems()
    const ticketingSystemUrl = RouteTicketingSystemsTickets({ ticketingSystemDisplayId })
    const ticketUrl = RouteTicketingSystemsTicket({ ticketingSystemDisplayId, ticketId })

    const breadcrumbsList = [{
      text: t('common.breadcrumbTicketingSystems'),
      url: ticketingSystemsUrl,
    }, {
      text: ticket.ticketingSystemName,
      url: ticketingSystemUrl,
    }, {
      text: ticket.externalId,
      url: ticketUrl,
    }, {
      text: t('common.breadcrumbTicketRequests'),
    }]
    return breadcrumbsList
  }

  const onRequestSelected = (request) => {
    setSelectedRequest(request)
  }

  const onSidebarClose = () => {
    setSelectedRequest(null)
  }

  if (ticketData && !ticket.ticketingSystemHasApiAccess) {
    return <ErrorPage404 />
  }

  const titleParams = {
    'ticketName': ticket.externalId,
    'tsName': ticket.ticketingSystemName,
    interpolation: { escapeValue: false },
  }
  const title = t('ticketRequestsPage.pageHeader', titleParams)
  const isLoading = isTicketFetching || isRequestsFetching

  const requestsCountText = t('appRequestsPage.requestsFound', { count: requestsCount })

  return (
    <MainWrap pageTitle={t('common.breadcrumbTicketRequests')}>
      <PartnerAppRequestsSidebar
        request={selectedRequest}
        onClose={onSidebarClose}
        ticketingSystemName={ticket.ticketingSystemName}
      />
      <Content loading={isLoading}>
        <Header
          title={title}
          breadcrumbsList={getBreadcrumbs()}
        />
        <CountRow>
          <StyledCount data-test='requests-count'>
            {requestsCountText}
          </StyledCount>
        </CountRow>
        <PartnerAppRequestsTable
          requests={requests}
          selectedRequestId={selectedRequest?.id}
          onRowClick={onRequestSelected}
        />
        <Pagination
          activePage={activePage}
          totalPages={totalPages}
          disabled={isLoading}
        />
      </Content>
    </MainWrap>
  )
}

export default TicketRequestsPage
